/**
 * Player-facing words for content ids (q-player-names-and-wayfinding, owner phone playtest
 * 2026-10-24: "Resource and enemy names are the raw-text versions and not player friendly ones").
 *
 * The cause: the quest log, the NPC rows, the gather/loot toasts and the HUD prompts printed the
 * WIRE's content ids (`craft fenroot-restorative`, `turned_in`, `questgiver, flavor`) because the
 * client had no single place that turned an id into the name the content gives it. This module is
 * that place. Every render path that shows a content id to a player goes through `nameOf` (or a
 * formatter built on it); `playerText.test.ts` scans the UI sources for a raw id interpolated
 * anywhere else, and renders every shipped quest through `objectiveText` to prove no id survives.
 *
 * Names come from the client content pack (P4.9, `ui/../content/pack.ts`), registered once on
 * pack load (`setContentNames`). Before the pack lands — or for an id the pack does not carry —
 * `humanizeId` gives a readable fallback ("fenroot-bark" → "Fenroot Bark"), never the raw id.
 */

import type { ClientContentPack, ContentCategory, QuestVerb } from '@great-cloud/shared/client';

/** Categories whose ids reach player-facing text. */
export type NamedCategory = Extract<
  ContentCategory,
  'items' | 'resources' | 'enemies' | 'npcs' | 'recipes' | 'biomes' | 'quests' | 'factions'
>;

const NAMED: readonly NamedCategory[] = [
  'items',
  'resources',
  'enemies',
  'npcs',
  'recipes',
  'biomes',
  'quests',
  'factions',
];

interface NameEntry {
  name: string;
  /** Enemies only: a boss reads "the Depths Sentinel". */
  isBoss?: boolean;
  /** Recipes only: the station it is made at, and the item it makes. */
  station?: string;
  output?: string;
}

let names: Partial<Record<NamedCategory, Map<string, NameEntry>>> = {};

/** Register the loaded pack's display names (App, on every pack sync). */
export function setContentNames(pack: ClientContentPack | null): void {
  const next: Partial<Record<NamedCategory, Map<string, NameEntry>>> = {};
  for (const category of NAMED) {
    const defs = pack?.categories[category];
    if (!defs) continue;
    const map = new Map<string, NameEntry>();
    for (const [id, def] of Object.entries(defs)) {
      const name = typeof def['name'] === 'string' ? def['name'] : '';
      if (!name) continue;
      const entry: NameEntry = { name };
      if (def['isBoss'] === true) entry.isBoss = true;
      if (typeof def['station'] === 'string') entry.station = def['station'];
      if (typeof def['output'] === 'string') entry.output = def['output'];
      map.set(id, entry);
    }
    next[category] = map;
  }
  names = next;
}

function entryOf(category: NamedCategory, id: string): NameEntry | undefined {
  return names[category]?.get(id);
}

/**
 * A readable stand-in for an id the pack does not name: separators become spaces and each word
 * is capitalised. Used only as a fallback — a content def always carries its own `name`.
 */
export function humanizeId(id: string): string {
  return id
    .split(/[-_\s]+/)
    .filter((w) => w.length > 0)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** The display name for a content id (the pack's `name`, else `humanizeId`). */
export function nameOf(category: NamedCategory, id: string | null | undefined): string {
  if (!id) return '';
  return entryOf(category, id)?.name ?? humanizeId(id);
}

/** An item OR a resource (loot, trade and inventory lines carry either under one id). */
export function stackName(id: string | null | undefined): string {
  if (!id) return '';
  return (entryOf('items', id) ?? entryOf('resources', id))?.name ?? humanizeId(id);
}

/** A craft objective's target is a recipe or an item (types.ts `QuestObjective`). */
export function craftTargetName(id: string | null | undefined): string {
  if (!id) return '';
  const recipe = entryOf('recipes', id);
  if (recipe?.output) return stackName(recipe.output);
  if (recipe) return recipe.name;
  return stackName(id);
}

/** The station a craft target (recipe or item id) is made at, when the pack says. */
export function craftStationOf(id: string | null | undefined): string | undefined {
  if (!id) return undefined;
  const direct = entryOf('recipes', id);
  if (direct?.station) return direct.station;
  for (const [, recipe] of names.recipes ?? []) {
    if (recipe.output === id && recipe.station) return recipe.station;
  }
  return undefined;
}

/** An enemy as a sentence object: "the Depths Sentinel" for a boss, else its name. */
export function enemyObject(id: string | null | undefined): string {
  if (!id) return '';
  const entry = entryOf('enemies', id);
  const name = entry?.name ?? humanizeId(id);
  return entry?.isBoss ? `the ${name}` : name;
}

/**
 * A server refusal token in words, for any reason no specific text covers:
 * "outOfRange" → "out of range", "insufficient_stamina" → "insufficient stamina".
 */
export function reasonWords(reason: string | null | undefined): string {
  if (!reason) return 'refused';
  return reason
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[-_\s]+/)
    .filter((w) => w.length > 0)
    .map((w) => w.toLowerCase())
    .join(' ');
}

/** Crafting stations (closed vocabulary, `CRAFTING_STATIONS`). */
const STATION_LABEL: Readonly<Record<string, string>> = {
  forge: 'Forge',
  'tanning-rack': 'Tanning Rack',
  'alchemy-table': 'Alchemy Table',
  workbench: 'Workbench',
  'engineering-bench': 'Engineering Bench',
  'furnishing-station': 'Furnishing Station',
};

export function stationLabel(station: string): string {
  return STATION_LABEL[station] ?? humanizeId(station);
}

/** The objective fields the quest log and tracker read (QuestObjectiveWire). */
export interface ObjectiveLike {
  verb: string;
  target?: string | null;
  item?: string | null;
}

/**
 * One objective as the player reads it — a verb phrase with display names:
 * "Gather Fenroot Bark", "Craft Fenroot Restorative at the Alchemy Table", "Defeat the Depths Sentinel",
 * "Speak with Warden Sel". Progress ("3/3") is the caller's, beside it.
 */
export function objectiveText(o: ObjectiveLike): string {
  const phrase = (OBJECTIVE_PHRASE as Partial<Record<string, (o: ObjectiveLike) => string>>)[
    o.verb
  ];
  return phrase
    ? phrase(o)
    : `${humanizeId(o.verb)} ${o.target ? humanizeId(o.target) : ''}`.trim();
}

/**
 * The phrase per quest verb — a total table over `QuestVerb` (AD-867), so a verb added to the
 * schema without player words fails the typecheck, and `questSignals.test.ts` asserts it too.
 */
export const OBJECTIVE_PHRASE: Readonly<Record<QuestVerb, (o: ObjectiveLike) => string>> = {
  gather: (o) => `Gather ${nameOf('resources', o.target)}`,
  craft: (o) => {
    // q-demo-crafting-clarity: the objective names WHERE ("…at the Alchemy Table"), so the
    // player knows a bench is part of the step; the wayfinder dial points at that bench.
    const station = craftStationOf(o.target);
    const at = station ? ` at the ${stationLabel(station)}` : '';
    return `Craft ${craftTargetName(o.target)}${at}`;
  },
  kill: (o) => `Defeat ${enemyObject(o.target)}`,
  talk: (o) => `Speak with ${nameOf('npcs', o.target)}`,
  deliver: (o) => `Bring ${stackName(o.item)} to ${nameOf('npcs', o.target)}`,
  visit: (o) => `Travel to ${nameOf('biomes', o.target)}`,
};

/** Quest status chips (QuestStatusWire). */
export function questStatusLabel(status: string): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'complete':
      return 'Ready to turn in';
    case 'turned_in':
      return 'Turned in';
    default:
      return humanizeId(status);
  }
}

/** NPC roles a player can act on; `flavor` is an authoring tag and is never shown. */
const ROLE_LABEL: Readonly<Record<string, string>> = {
  questgiver: 'Quests',
  vendor: 'Merchant',
  trainer: 'Trainer',
  services: 'Services',
};

/** An NPC's role tags in player words, internal tags dropped ("Quests · Merchant"). */
export function npcRolesText(roles: readonly string[]): string {
  return roles
    .map((r) => ROLE_LABEL[r])
    .filter((r): r is string => r !== undefined)
    .join(' · ');
}

/**
 * A raw content id or snake/kebab token: two or more lowercase words joined by `-` or `_`.
 * What `playerText.test.ts` asserts never reaches a player-facing string.
 */
export const RAW_ID_PATTERN = /\b[a-z0-9]+(?:[-_][a-z0-9]+)+\b/;
